import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSupplementStore, ProtocolItem } from '@stores/v4/useSupplementStore';
import { SupplementChecklist } from './supplement/SupplementChecklist';
import { BioLoadPreview } from '../shared/BioLoadPreview';
import { NutritionCompute } from '@/shared/logic/metabolic/nutrition-compute';

interface SupplementBodyProps {
    data: any;
    updatePayload: (updates: any) => void;
}

/**
 * SupplementBody: Intelligent Checklist Editor for Supplementation.
 * V9.7: Support for custom dosages and JIT aggregation.
 */
export const SupplementBody: React.FC<SupplementBodyProps> = ({ data, updatePayload }) => {
    const payload = data.payload || {};
    const { activeProtocol, fetchActiveProtocol } = useSupplementStore();
    
    const [selectedWindow, setSelectedWindow] = useState<string | null>(payload.window || payload.timing_window || null);
    const [isBioLoadExpanded, setIsBioLoadExpanded] = useState(true);
    const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        if (payload.items_checked) {
            payload.items_checked.forEach((id: string) => initial[id] = true);
        }
        return initial;
    });

    // Local override for dosages in this specific session
    const [localDosages, setLocalDosages] = useState<Record<string, number>>(() => {
        const initial: Record<string, number> = {};
        if (payload.items) {
            payload.items.forEach((it: any) => {
                initial[it.id] = it.dosage_count;
            });
        }
        return initial;
    });

    useEffect(() => {
        if (!activeProtocol) fetchActiveProtocol();
    }, [activeProtocol, fetchActiveProtocol]);

    const totals = useMemo(() => {
        if (!payload.items || payload.items.length === 0) return null;
        return NutritionCompute.calculateLedgerTotals(payload.items);
    }, [payload.items]);

    useEffect(() => {
        if (activeProtocol && selectedWindow && Object.keys(checkedItems).length === 0) {
            const windowItems = activeProtocol.items.filter(it => it.timing_window === selectedWindow || selectedWindow === 'ALL');
            if (windowItems.length > 0) {
                const newChecked: Record<string, boolean> = {};
                windowItems.forEach(it => newChecked[it.id] = true);
                syncCrystallizedState(newChecked, localDosages, selectedWindow);
            }
        }
    }, [activeProtocol, selectedWindow]);

    const syncCrystallizedState = useCallback((newChecked: Record<string, boolean>, dosages: Record<string, number>, window: string) => {
        setCheckedItems(newChecked);

        const selectedItems: any[] = [];
        const selectedNames: string[] = [];
        const checkedList: string[] = [];

        activeProtocol?.items.forEach(it => {
            if (newChecked[it.id]) {
                checkedList.push(it.id);
                selectedNames.push(it.catalog_item?.product_name || "");
                const cat = it.catalog_item;
                if (cat) {
                    selectedItems.push({
                        id: it.id,
                        item_name: cat.product_name,
                        type: 'SUPPLEMENT',
                        measure_unit: cat.measure_unit,
                        baseline_micros: cat.baseline_micros,
                        // Priority: Local override > Protocol default
                        dosage_count: dosages[it.id] !== undefined ? dosages[it.id] : it.dosage_count
                    });
                }
            }
        });

        updatePayload({
            ...payload,
            timing_window: window === 'ALL' ? 'MIXED' : window,
            compound_name: (window === 'ALL' ? 'CUSTOM' : window).replace(/_/g, ' ') + " STACK",
            items_checked: checkedList,
            dosage: `${selectedNames.length} items confirmed`,
            items: selectedItems
        });
    }, [activeProtocol, payload, updatePayload]);

    const handleUpdateDosage = (itemId: string, amount: number) => {
        const newDosages = { ...localDosages, [itemId]: amount };
        setLocalDosages(newDosages);
        syncCrystallizedState(checkedItems, newDosages, selectedWindow || 'AD-HOC');
    };

    const handleToggleAll = (checked: boolean) => {
        if (!activeProtocol || !selectedWindow) return;
        const windowItems = activeProtocol.items.filter(it => selectedWindow === 'ALL' || it.timing_window === selectedWindow);
        const newChecked = { ...checkedItems };
        windowItems.forEach(it => { newChecked[it.id] = checked; });
        syncCrystallizedState(newChecked, localDosages, selectedWindow);
    };

    const inputStyle: React.CSSProperties = { 
        all: 'unset', backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '10px', 
        padding: '12px', color: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box'
    };

    const windows = ['ALL', 'MORNING', 'LUNCH', 'PRE_WO', 'POST_WO', 'SLEEP', 'AD-HOC'];

    const toggleItem = (item: ProtocolItem) => {
        const newChecked = { ...checkedItems, [item.id]: !checkedItems[item.id] };
        syncCrystallizedState(newChecked, localDosages, selectedWindow || 'AD-HOC');
    };

    return (
        <div className="animate-in fade-in slide-in-from-top-2">
            <SupplementChecklist 
                windows={windows}
                selectedWindow={selectedWindow}
                setSelectedWindow={(win) => {
                    setSelectedWindow(win);
                    if (win === 'AD-HOC') setCheckedItems({});
                }}
                activeProtocol={activeProtocol}
                checkedItems={checkedItems}
                toggleItem={toggleItem}
                onToggleAll={handleToggleAll}
                onUpdateDosage={handleUpdateDosage}
                customDosages={localDosages}
            />

            {/* 3. AD-HOC / MANUAL */}
            {selectedWindow === 'AD-HOC' && (
                <>
                    <div style={{fontSize: '9px', fontWeight: '900', color: '#444', marginBottom: '8px', letterSpacing: 1, marginTop: '20px'}}>COMPOUND NAME</div>
                    <input 
                        type="text" placeholder="e.g. Magnesium Citrate" 
                        value={payload.compound_name || ""}
                        onChange={(e) => updatePayload({ ...payload, compound_name: e.target.value })}
                        style={{...inputStyle, marginBottom: '20px'}} 
                    />
                    <div style={{fontSize: '9px', fontWeight: '900', color: '#444', marginBottom: '8px', letterSpacing: 1}}>DOSAGE</div>
                    <input 
                        type="text" placeholder="e.g. 400mg" 
                        value={payload.dosage || ""}
                        onChange={(e) => updatePayload({ ...payload, dosage: e.target.value })}
                        style={inputStyle} 
                    />
                </>
            )}

            {totals && (
                <BioLoadPreview 
                    data={totals} 
                    isExpanded={isBioLoadExpanded} 
                    setIsExpanded={setIsBioLoadExpanded} 
                    title="PROTOCOL BIO-LOAD ANALYSIS"
                    accentColor="#a855f7"
                />
            )}
        </div>
    );
};
